import { Guest } from "@/types/Guest";
import {
  CalendarConfig,
  WalletConfig,
  WalletField,
} from "@/types/DocumentTemplate";
import { ConditionGroup } from "@/types/EventRule";
import type { Field } from "@/types/Field";
import { evaluateConditionGroup } from "./evaluate-condition";
import { EVENT_VARIABLE_KEYS } from "@/lib/utils/event-variables";
import { parseToUTCDate } from "./parser";
import {
  formatAdditionalFieldValue,
  localizedBoolean,
} from "./additional-field-format";
import {
  AF_IMAGE_PLACEHOLDER_DATA_URI,
  parseAfImageToken,
} from "./af-image-token";
import {
  formatApptDate,
  formatApptTime,
  humanizeAppointmentStatus,
  humanizeSeatStatus,
  humanizeEmailStatus,
} from "@/components/A4Builder/appointment-record";
import { hasNonEmptyConditions } from "@/components/ConditionBuilder/condition-utils";

/**
 * Optional formatting context for the builder PREVIEW so `{{additionalFields.x}}`
 * renders by the field's declared type (localized boolean / date / currency /
 * number) like the real send, instead of raw `String(value)`. When omitted the
 * legacy raw behavior is preserved (non-preview callers unaffected).
 */
export interface PreviewFormatContext {
  /** All additional-field definitions, keyed by field NAME (the token key). */
  fieldsByName?: ReadonlyMap<string, Field>;
  /** Event timezone for DATE_TIME rendering (matches the send). */
  timeZone?: string | null;
  /** Resolved RENDER language (effLang) — the language the body actually renders
   *  in (guest.language → template primary). Typed values (dates/booleans/
   *  currency/status) format in this, matching the consumer's `__renderLang`. */
  lang?: string | null;
  /** HTML context (email body / send preview) → URL additional fields render as
   *  anchors. False for plain-text (subject) / React-escaped canvas contexts. */
  asHtml?: boolean;
  /** Pre-resolved presigned GET URLs for the preview guest's IMAGE additional
   *  fields, keyed `additionalFields.<name>` (see useAfImagePreviewUrls). Feeds
   *  the image-block token pass: a `{{additionalFields.x}}` src becomes the
   *  guest's real image in preview, else the neutral placeholder. */
  afImageUrls?: ReadonlyMap<string, string>;
}

/** Represents a CraftJS node with text properties that may contain variable placeholders */
interface CraftJsTextNode {
  props?: {
    texts?: Record<string, string>;
    displayCondition?: ConditionGroup;
    [key: string]: unknown;
  };
  parent?: string;
  nodes?: string[];
  linkedNodes?: Record<string, string>;
  [key: string]: unknown;
}

/**
 * Preview-only: drop nodes whose `displayCondition` does NOT match the given
 * guest, so the editor preview matches what the recipient will actually get
 * (the consumer strips the equivalent `VLT-IF` markers at send time).
 *
 * Deleting a node from the flat CraftJS map is not enough — CraftJS deserialize
 * throws on dangling refs — so we also detach the id from its parent's `nodes`
 * (and `linkedNodes`) and recursively delete the whole subtree. ROOT is never
 * pruned. Exported for tests.
 */
export const pruneConditionalNodes = (json: string, guest: Guest): string => {
  try {
    const data: Record<string, CraftJsTextNode> = JSON.parse(json);
    const guestData = guest as unknown as Record<string, unknown>;

    const deleteSubtree = (id: string) => {
      const node = data[id];
      if (!node) return;
      const children = [
        ...(Array.isArray(node.nodes) ? node.nodes : []),
        ...(node.linkedNodes ? Object.values(node.linkedNodes) : []),
      ];
      delete data[id];
      children.forEach(deleteSubtree);
    };

    // Snapshot ids — we mutate `data` while iterating.
    for (const id of Object.keys(data)) {
      if (id === "ROOT") continue;
      const node = data[id];
      if (!node) continue; // already removed as a descendant
      const cond = node.props?.displayCondition;
      if (!cond || !hasNonEmptyConditions(cond)) continue;
      if (evaluateConditionGroup(cond, guestData)) continue;

      const parent = node.parent ? data[node.parent] : undefined;
      if (parent) {
        if (Array.isArray(parent.nodes)) {
          parent.nodes = parent.nodes.filter((n) => n !== id);
        }
        if (parent.linkedNodes) {
          for (const [slot, target] of Object.entries(parent.linkedNodes)) {
            if (target === id) delete parent.linkedNodes[slot];
          }
        }
      }
      deleteSubtree(id);
    }

    return JSON.stringify(data);
  } catch {
    return json;
  }
};

/**
 * Gets a nested property value from an object using dot notation
 */
export const getNestedValue = (obj: object, path: string): unknown => {
  return path.split(".").reduce<unknown>((current, key) => {
    if (
      current !== null &&
      current !== undefined &&
      typeof current === "object"
    ) {
      // Own-property only — never descend into prototype members so a
      // hand-typed `{{mainGuest.toString}}` / `{{x.constructor}}` resolves to
      // "" (parity with the backend renderer's `resolveNestedPath`), instead of
      // emitting native-code junk in preview.
      if (!Object.prototype.hasOwnProperty.call(current, key)) return undefined;
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
};

// ──────── Activity-rollup var formatting (client-side preview + wallet) ────────
// The email send (consumer `enrichGuestActivity`) and the PDF service
// (`enrichGuestActivityVarsPayload`) humanize these fully — localized, in the
// event timezone, with offering/treatment/lane/room/template NAMES resolved.
// This pure util has no i18n / timezone / option-map context, so it does a
// best-effort clean render (browser locale, title-cased status) and BLANKS the
// id→name vars rather than leaking raw hex ids into a client-rendered wallet
// pass. Keys NOT listed here fall through to the plain `String(value)` path.
const ACTIVITY_DATE_KEYS = new Set([
  "nextApptAt", "lastEmailedAt", "lastEmailOpenedAt", "lastEmailClickedAt",
  "portalVisitedAt", "websiteLoginAt", "createdAt", "updatedAt",
]);
const ACTIVITY_STATUS_KEYS = new Set(["apptStatuses", "seatStatuses", "lastEmailStatus"]);
const ACTIVITY_BOOL_KEYS = new Set(["hasOpenRequest"]);
// Resolve to NAMES at real send time; FE can't here → blank, never raw ids.
const ACTIVITY_NAME_KEYS = new Set([
  "apptOfferings", "apptTreatments", "apptLanes", "seatRooms",
  "emailedTemplates", "emailedCampaigns",
]);

const titleCaseEnum = (s: string): string =>
  s
    // Split UPPER_SNAKE (NO_SHOW) AND camelCase (checkedIn) so seat statuses
    // like "checkedIn" render "Checked In", not "CheckedIn".
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Localized humanizer per activity-status var (matches the send). apptStatuses →
// appointment labels, seatStatuses → seat labels, lastEmailStatus → email labels.
const ACTIVITY_STATUS_HUMANIZERS: Record<
  string,
  (v: unknown, lang?: string | null) => string
> = {
  apptStatuses: humanizeAppointmentStatus,
  seatStatuses: humanizeSeatStatus,
  lastEmailStatus: humanizeEmailStatus,
};

// Action/link tokens minted only at send time (signed URLs). In preview they
// have no guest value, so the raw `String(value)` path would blank them → a
// button renders `href=""` and looks broken. Substitute an inert `#` so the
// admin sees the button as present (not broken) without a real signed link.
const MINT_URL_KEYS = new Set([
  "acceptInvitationUrl", "declineInvitationUrl",
  "confirmAppointmentUrl", "declineAppointmentUrl",
  "portalLink", "unsubscribeUrl", "viewInBrowserUrl",
]);

/**
 * Resolve a `{{key}}` to its display string for the client-side renderer.
 * Activity rollup keys get best-effort humanization; everything else keeps the
 * existing `String(value)` behavior (empty when missing).
 */
export const resolveVarValue = (
  guest: Guest,
  key: string,
  ctx?: PreviewFormatContext,
): string => {
  if (ACTIVITY_NAME_KEYS.has(key)) return "";
  // Preview-only render language for typed values (effLang from the ctx), so a
  // German body formats dates/booleans/status in German even if the guest has no
  // language set. Falls back to the guest's own language.
  const fmtLang = ctx?.lang ?? guest.language;
  // Mint-class URL tokens (accept/decline/portal/unsubscribe/view-in-browser)
  // are only signed at send. Show an inert `#` in preview so the button isn't
  // mistaken for broken. Only in preview (ctx present) — non-preview callers keep
  // the raw blank behavior.
  if (ctx && MINT_URL_KEYS.has(key)) return "#";
  const value = getNestedValue(guest, key);
  if (value === undefined || value === null) return "";
  // Additional (custom) field → format by its declared dataType so the preview
  // matches the real send (localized boolean / date / currency / number / URL).
  // Recipient-scoped and mainGuest-scoped both resolve to `additionalFields.<name>`.
  if (ctx?.fieldsByName) {
    const afIdx = key.lastIndexOf("additionalFields.");
    if (afIdx !== -1) {
      const name = key.slice(afIdx + "additionalFields.".length);
      const field = ctx.fieldsByName.get(name);
      if (field) {
        return formatAdditionalFieldValue(
          value,
          field,
          fmtLang,
          ctx.timeZone,
          ctx.asHtml,
        );
      }
    }
  }
  if (ACTIVITY_DATE_KEYS.has(key)) {
    // Backend serializes dates with a trailing IANA suffix ("…Z[UTC]") that
    // raw `new Date()` rejects → blank. parseToUTCDate strips it (same fix the
    // date picker uses).
    const d = value instanceof Date ? value : parseToUTCDate(String(value));
    if (!d || Number.isNaN(d.getTime())) return "";
    // In preview, format in the event timezone + render language (matching the
    // send's `formatRollupInstant`) instead of the browser's tz/locale.
    if (ctx) {
      const iso = d.toISOString();
      const datePart = formatApptDate(iso, fmtLang, ctx.timeZone);
      const timePart = formatApptTime(iso, fmtLang, ctx.timeZone);
      return datePart && timePart ? `${datePart} ${timePart}` : datePart || timePart;
    }
    return d.toLocaleString();
  }
  if (ACTIVITY_STATUS_KEYS.has(key)) {
    const humanize = ACTIVITY_STATUS_HUMANIZERS[key];
    const arr = Array.isArray(value) ? value : [value];
    return arr
      .map((s) => (humanize ? humanize(s, fmtLang) : titleCaseEnum(String(s))))
      .filter((s) => s.length > 0)
      .join(", ");
  }
  if (ACTIVITY_BOOL_KEYS.has(key)) {
    const truthy =
      typeof value === "boolean" ? value : String(value).toLowerCase() === "true";
    // Localize in preview (ctx present) to match the send; legacy path keeps EN.
    return ctx ? localizedBoolean(truthy, fmtLang) : truthy ? "Yes" : "No";
  }
  return String(value);
};

/**
 * Token regex for `{{...}}` placeholders. `[^{}]+` (not `[a-zA-Z0-9_.]+`) so a
 * custom-field NAME containing spaces / umlauts / punctuation (e.g.
 * `{{additionalFields.Room Category}}`, `{{additionalFields.Größe}}`) still
 * resolves — matching the consumer's tolerant `{{(.*?)}}` and the (now aligned)
 * backend `VAR_PATTERN`, so preview / email / PDF agree.
 */
const VAR_TOKEN_RE = /\{\{([^{}]+)\}\}/g;

/** Every `{{token}}` name in a string, trimmed, in order of appearance. */
export const extractTokens = (text: string): string[] =>
  [...text.matchAll(VAR_TOKEN_RE)].map((m) => m[1].trim());

/**
 * Whether the CLIENT can decide what this token resolves to for this guest.
 *
 * The distinction `resolveVarValue` throws away: it answers `""` both for a
 * field the guest genuinely left blank and for one the projection never carried
 * — and only the first says anything about the delivered pass. `getNestedValue`
 * keeps them apart, returning `undefined` only for a key that is not an own
 * property. `ACTIVITY_NAME_KEYS` are excluded because they resolve to `""` by
 * design (the client refuses to render raw ids) while the send fills them.
 *
 * Deliberately conservative: unknown ⇒ say nothing.
 *
 * <p>`eventLoaded` separates the two kinds of absence. An EVENT key that is not
 * on the object means the event carries no such value (`buildEventVariables`
 * skips empty sources) — that is knowledge, and the send will find the same
 * emptiness. A GUEST key that is not on the object was never projected, and
 * about that we know nothing.
 */
export const knowsToken = (
  guest: object,
  key: string,
  eventLoaded: boolean,
): boolean => {
  if (ACTIVITY_NAME_KEYS.has(key)) return false;
  if (EVENT_VARIABLE_KEYS.has(key)) return eventLoaded;
  return getNestedValue(guest, key) !== undefined;
};

/**
 * Replaces variables in CraftJS JSON with actual guest values
 */
export const replaceVariablesInCraftJs = (
  json: string,
  guest: Guest,
  ctx?: PreviewFormatContext,
): string => {
  try {
    // First drop nodes hidden by their displayCondition for this guest, then
    // substitute variables on what remains.
    const data: Record<string, CraftJsTextNode> = JSON.parse(
      pruneConditionalNodes(json, guest)
    );

    // Recursively process all nodes
    const processNode = (node: CraftJsTextNode): CraftJsTextNode => {
      if (node.props?.texts) {
        // Process each language variant
        const texts = node.props.texts;
        Object.keys(texts).forEach((lang) => {
          // Single-pass callback replace — avoids building a per-token RegExp
          // from the raw token (which broke on names with regex-special chars).
          texts[lang] = texts[lang].replace(VAR_TOKEN_RE, (_m, key: string) =>
            resolveVarValue(guest, key, ctx),
          );
        });
      }

      // Image-block "Gastbild" pass: an imageUrls value that IS one
      // `{{additionalFields.<name>}}` token resolves to the preview guest's
      // presigned URL (pre-fetched into ctx.afImageUrls — the replacer is
      // sync), falling back to the neutral placeholder when the guest has no
      // value / URLs weren't resolved. The real send substitutes cid-inline /
      // data-URI images instead; this only serves FE previews.
      const imageUrls = node.props?.imageUrls;
      if (imageUrls && typeof imageUrls === "object" && !Array.isArray(imageUrls)) {
        const map = imageUrls as Record<string, unknown>;
        for (const lang of Object.keys(map)) {
          const src = map[lang];
          if (typeof src !== "string") continue;
          const fieldName = parseAfImageToken(src);
          if (!fieldName) continue;
          const resolved = ctx?.afImageUrls?.get(`additionalFields.${fieldName}`);
          // Placeholder when unresolved — a raw stored value (R2 key object)
          // is not a fetchable URL, so "has image but URL unknown" and "no
          // image" both render the neutral tile.
          map[lang] = resolved ?? AF_IMAGE_PLACEHOLDER_DATA_URI;
        }
      }

      return node;
    };

    // Process all nodes in the data structure
    Object.keys(data).forEach((key) => {
      data[key] = processNode(data[key]);
    });

    return JSON.stringify(data);
  } catch {
    return json;
  }
};

/**
 * Replaces {{base64:field1:field2}} placeholders with base64-encoded values
 * Used for credential prepopulation in event path URLs
 */
export const replaceBase64Variables = (
  text: string,
  guest: Guest
): string => {
  return text.replace(/{{base64:([a-zA-Z0-9_.]+):([a-zA-Z0-9_.]+)}}/g, (_match, field1, field2) => {
    const val1 = getNestedValue(guest, field1);
    const val2 = getNestedValue(guest, field2);
    const str1 = val1 !== undefined && val1 !== null ? String(val1) : "";
    const str2 = val2 !== undefined && val2 !== null ? String(val2) : "";
    if (!str1 && !str2) return "";
    return btoa(`${str1}:${str2}`);
  });
};

/**
 * Replaces plain text {{variable}} placeholders with actual guest values
 * Use for non-HTML fields like subject lines
 */
export const replaceVariablesInText = (
  text: string,
  guest: Guest,
  ctx?: PreviewFormatContext,
): string => {
  let result = replaceBase64Variables(text, guest);
  result = result.replace(VAR_TOKEN_RE, (_match, variableName) =>
    resolveVarValue(guest, variableName, ctx),
  );
  return result;
};

/**
 * Replaces {{variable}} placeholders in HTML with actual guest values.
 */
export const replaceVariablesInHtml = (
  html: string,
  guest: Guest,
  ctx?: PreviewFormatContext,
): string => {
  // HTML context → URL additional fields render as anchors.
  const htmlCtx = ctx ? { ...ctx, asHtml: true } : ctx;
  let result = replaceBase64Variables(html, guest);
  result = result.replace(VAR_TOKEN_RE, (_match, variableName) =>
    resolveVarValue(guest, variableName, htmlCtx),
  );
  return result;
};

const replaceInMultilang = (
  map: Record<string, string> | undefined,
  guest: Guest,
  ctx?: PreviewFormatContext,
): Record<string, string> | undefined => {
  if (!map) return map;
  const result: Record<string, string> = {};
  for (const [lang, text] of Object.entries(map)) {
    result[lang] = replaceVariablesInText(text, guest, ctx);
  }
  return result;
};

const replaceInWalletFields = (
  fields: WalletField[] | undefined,
  guest: Guest,
  ctx?: PreviewFormatContext,
): WalletField[] | undefined =>
  fields?.map((f) => ({
    ...f,
    label: replaceInMultilang(f.label, guest, ctx),
    value: replaceInMultilang(f.value, guest, ctx),
  }));

export const replaceVariablesInWalletConfig = (
  config: WalletConfig,
  guest: Guest,
  ctx?: PreviewFormatContext,
): WalletConfig => ({
  ...config,
  organizationName: replaceInMultilang(config.organizationName, guest, ctx),
  logoText: replaceInMultilang(config.logoText, guest, ctx),
  description: replaceInMultilang(config.description, guest, ctx),
  headerFields: replaceInWalletFields(config.headerFields, guest, ctx),
  primaryFields: replaceInWalletFields(config.primaryFields, guest, ctx),
  secondaryFields: replaceInWalletFields(config.secondaryFields, guest, ctx),
  auxiliaryFields: replaceInWalletFields(config.auxiliaryFields, guest, ctx),
  backFields: replaceInWalletFields(config.backFields, guest, ctx),
  // barcodeValue / barcodeAltText are MACHINE-READABLE (scanned payload) — never
  // apply type formatting (a localized "1. Juli 2026" would corrupt the encoded
  // value); keep the raw substituted value.
  barcodeValue: config.barcodeValue
    ? replaceVariablesInText(config.barcodeValue, guest)
    : config.barcodeValue,
  barcodeAltText: config.barcodeAltText
    ? replaceVariablesInText(config.barcodeAltText, guest)
    : config.barcodeAltText,
});

export const replaceVariablesInCalendarConfig = (
  config: CalendarConfig,
  guest: Guest,
  ctx?: PreviewFormatContext,
): CalendarConfig => ({
  ...config,
  summary: replaceInMultilang(config.summary, guest, ctx),
  description: replaceInMultilang(config.description, guest, ctx),
  location: replaceInMultilang(config.location, guest, ctx),
  organizerName: config.organizerName
    ? replaceVariablesInText(config.organizerName, guest, ctx)
    : config.organizerName,
  // organizerEmail / url are technical fields — keep raw (a formatted date in a
  // URL/email would break it).
  organizerEmail: config.organizerEmail
    ? replaceVariablesInText(config.organizerEmail, guest)
    : config.organizerEmail,
  url: config.url ? replaceVariablesInText(config.url, guest) : config.url,
});
